import React, { useState } from "react";
import { Button, Card } from "antd";
import { DeleteOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import BaseTable from "../../../../components/BaseTable";
import Column from "../../../../components/Column";
import Row from "../../../../components/Row";
import ActionHeader from "../../../../components/ActionHeader";
import usePageState from "../../../../hooks/usePageState";
import useConfirmAction from "../../../../hooks/useConfirmAction";
import useForm from "../../../../hooks/useForm";
import * as Yup from "yup";
import AppInputDropdown from "../../../../components/AppInputDropdown";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import {
  CreateInventoryLogRequest,
  FilterInventoryLogRequest,
  InventoryLogModel,
} from "../../../../../infrastructure/model/InventoryOnHandModel";
import { useDateRangeSelector } from "../../../../hooks/useDateRangeSelector";
import {
  createInventoryLogUseCase,
  deleteInventoryLogUseCase,
  getInventoryLogUseCase,
} from "../../../../../applications/useCase/ApiUseCase/InventoryLogApiUseCase";
import AppInputDate from "../../../../components/AppInputDate";
import { getListRawMaterialUseCase } from "../../../../../applications/useCase/ApiUseCase/RawMaterialApiUseCase";
import { RawMaterialModel } from "../../../../../infrastructure/model/RawMaterialModel";
import AppInputNumber from "../../../../components/AppInputNumber";
import { currencyFormat } from "../../../../../applications/utils/NumberUtils";
import AppInputText from "../../../../components/AppInputText/AppInputText";
import { DATE_RANGE_OPTION_ENUM } from "../../../../../applications/constants/AppConst";

function ImportInventoryManager() {
  const [dataTable, setDataTable] = useState<InventoryLogModel[]>([]);
  const [itemSelected, setItemSelected] = useState<InventoryLogModel>();
  const { dialog: confirmDeleteDialog, confirm: confirmDelete } = useConfirmAction({
    onConfirm: deleteInventoryLog,
  });
  const [listRaw, setListRaw] = useState<RawMaterialModel[]>([]);
  const { isLoading, setLoading, showError, showSuccess, showClientError } = usePageState();
  const { endDate, startDate, DateRangeSelectorComponent, validateDateRange } =
    useDateRangeSelector({
      initSelectOptionDate: DATE_RANGE_OPTION_ENUM.CUSTOM,
    });

  const [filter, setFilter] = useState<FilterInventoryLogRequest>({
    page: 1,
    limit: 50,
    end_date: endDate!,
    raw_name: "",
    start_date: startDate!,
  });
  const [total, setTotal] = useState<number>(0);

  const createRawMaterialSchema = Yup.object({
    import_date: Yup.number()
      .required("Ngày nhập kho là bắt buộc")
      .moreThan(0, "Ngày nhập kho phải > 0"),
    raw_id: Yup.number()
      .required("Nguyên vật liệu là bắt buộc")
      .moreThan(0, "Nguyên vật liệu phải > 0"),
    quantity: Yup.number()
      .required("Số lượng nhập là bắt buộc")
      .moreThan(0, "Số lượng nhập phải > 0"),
    unit_price: Yup.number()
      .required("Giá tiền nhập là bắt buộc")
      .moreThan(0, "Giá tiền nhập phải > 0"),
  });

  const formik = useForm<CreateInventoryLogRequest>({
    initialValues: {
      import_date: 0,
      quantity: 0,
      raw_id: 0,
      unit_price: 0,
    },
    validationSchema: createRawMaterialSchema,
    onSubmit: async (values, formikHelpers) => {
      const result = await createInventoryLogUseCase({
        request: values,
        setLoading,
        showError,
        showSuccess,
      });
      if (result) {
        setDataTable([result, ...dataTable]);
        formik.resetForm();
      }
    },
  });

  async function getAllRawData() {
    const dataRes = await getListRawMaterialUseCase({
      setLoading,
      showError,
      showSuccess,
    });
    setListRaw(dataRes);
  }

  async function getListData() {
    if (!validateDateRange) return;
    const dataRes = await getInventoryLogUseCase({
      request: {
        ...filter,
        start_date: startDate!,
        end_date: endDate!,
      },
      setLoading,
      showError,
    });
    setDataTable(dataRes.data);
    setTotal(dataRes.total);
    setItemSelected(undefined);
    formik.resetForm();
  }

  async function deleteInventoryLog() {
    if (!itemSelected?.id) return;
    const dataRes = await deleteInventoryLogUseCase({
      id: itemSelected.id,
      setLoading,
      showError,
      showSuccess,
    });
    if (dataRes) {
      setDataTable(dataTable.filter((e) => e.id !== itemSelected.id));
      setItemSelected(undefined);
    }
  }

  useAsyncEffect(async () => {
    await Promise.all([getListData(), getAllRawData()]);
  }, []);

  return (
    <Column className="h-100 w-100 p-3">
      <Column className="gap-2 pb-2">
        <Card>
          <Row className="w-100 gap-2">
            <Column className="col-5">
              <AppInputDate
                orientation="col"
                label="Ngày nhập kho"
                isRequired
                currentValue={formik.values.import_date}
                onChangeValue={(e) => formik.updateFieldValue("import_date", e!)}
                errorText={formik.getFormErrorMessage("import_date")}
              />
            </Column>
            <Column className="col-5">
              <AppInputDropdown
                orientation="col"
                label="Nguyên vật liệu"
                options={listRaw?.map((e) => ({
                  label: e.name,
                  value: e.id,
                }))}
                isRequired
                currentValue={formik.values.raw_id}
                onChangeValue={(e) => formik.updateFieldValue("raw_id", e)}
                errorText={formik.getFormErrorMessage("raw_id")}
              />
            </Column>

            <Column className="col-5">
              <AppInputNumber
                orientation="col"
                label="Số lượng"
                currentValue={formik.values.quantity}
                onChangeValue={(e) => formik.updateFieldValue("quantity", e)}
                errorText={formik.getFormErrorMessage("quantity")}
              />
            </Column>
            <Column className="col-5">
              <AppInputNumber
                orientation="col"
                label="Đơn giá"
                currentValue={formik.values.unit_price}
                onChangeValue={(e) => formik.updateFieldValue("unit_price", e)}
                errorText={formik.getFormErrorMessage("unit_price")}
              />
            </Column>
            <Button
              type="primary"
              htmlType="submit"
              style={{ marginTop: "25px" }}
              onClick={() => formik.checkErrorAndSubmit(showClientError)}
            >
              Lưu
            </Button>
          </Row>
        </Card>
        <Row className="w-100 gap-2 p-1">
          <Column className="col-6">
            <AppInputText
              currentValue={filter.raw_name}
              label="Tên nguyên vật liệu"
              additionProps={{
                placeholder: "Nhập tên nguyên liệu...",
              }}
              orientation="col"
              onTextChange={(e) => setFilter({ ...filter, raw_name: e })}
            />
          </Column>
          <DateRangeSelectorComponent />

          <Column style={{ paddingTop: "19px" }}>
            <Button
              icon={<SearchOutlined />}
              type="primary"
              onClick={() => getListData()}
              className="w-100"
            >
              Tìm kiếm
            </Button>
          </Column>
        </Row>
        {/* Danh sách Table */}
        <ActionHeader
          rowCount={total}
          actions={[
            {
              icon: <ReloadOutlined />,
              userClick: getListData,
              title: "Làm mới",
              isNormal: true,
            },
            {
              icon: <DeleteOutlined />,
              userClick: confirmDelete,
              title: "Xóa",
              disabled: !itemSelected?.id,
              isNormal: true,
              color: "danger",
            },
          ]}
        />
      </Column>

      <BaseTable
        dataSource={dataTable}
        rowSelection={{
          type: "radio",
          selectedRowKeys: itemSelected?.id ? [itemSelected.id] : [],
          onSelect(record) {
            setItemSelected(record);
          },
        }}
        loading={isLoading}
        columns={[
          {
            title: "Ngày nhập kho",
            dataIndex: "importDate",
            key: "importDate",
            render(value, record, index) {
              return new Date(value).toLocaleDateString("vi-VN");
            },
          },
          {
            title: "Nguyên vật liệu",
            dataIndex: "rawName",
            key: "rawName",
          },
          {
            title: "Số lượng",
            dataIndex: "quantity",
            key: "quantity",
            render(value, record, index) {
              return `${value}  (${record.uomName})`;
            },
          },
          {
            title: "Đơn giá",
            dataIndex: "unitPrice",
            key: "unitPrice",
            render(value, record, index) {
              return currencyFormat(value);
            },
          },
          {
            title: "Thành tiền",
            dataIndex: "totalPrice",
            key: "totalPrice",
            render(value, record, index) {
              return currencyFormat(value);
            },
          },
        ]}
        rowKey="id"
      />
      {confirmDeleteDialog}
    </Column>
  );
}

export default ImportInventoryManager;
